// Fixed engagement phases and steps (mirrors App\Project\Engagement\Engagement)
export const PHASES = {
  acceptance: ['client_evaluation', 'pre_engagement_checks', 'engagement_letter'],
  planning: ['business_understanding', 'risk_materiality', 'audit_strategy'],
  execution: ['design_implementation', 'operating_effectiveness', 'evidence_collection'],
  reporting: ['evidence_review', 'deficiency_analysis', 'reporting', 'closure']
};

export const PHASE_KEYS = Object.keys(PHASES);

// Phases whose steps are always included; only acceptance and planning steps can be left out
export const ALWAYS_INCLUDED_PHASES = ['execution', 'reporting'];

// Phases only Managers can edit
export const MANAGER_PHASES = ['acceptance', 'planning'];

export const STEP_STATUSES = ['not_started', 'in_progress', 'completed'];

export const ROLES = ['manager', 'associate'];

export const COLLECTION_OTHER = 'engagement_other';

// Steps whose progress follows the tasks instead of being set by hand
export const DERIVED_STEPS = ['design_implementation', 'operating_effectiveness', 'evidence_collection'];

// Steps without their own document slot: D&I and OE follow the task status, and reporting
// files are kept with the generated reports on the Reporting tab
export const STEPS_WITHOUT_DOCUMENTS = ['design_implementation', 'operating_effectiveness', 'evidence_review', 'reporting', 'closure'];

export function allSteps() {
  return [].concat(...PHASE_KEYS.map(phase => PHASES[phase]));
}

export function collectionOf(step) {
  return 'engagement_' + step;
}

export function phaseOf(step) {
  return PHASE_KEYS.find(phase => PHASES[phase].includes(step)) || null;
}

// Engagement projects: one time control assessments
export function isEngagement(project) {
  return !!project && project.type === 'control_assessment' && project.control_type === 'one time';
}

export function engagementPermissions(project) {
  return (project && project.engagement_permissions) || {};
}

// Whether the current user can change a step of the given phase
export function canEditPhase(project, phase) {
  const permissions = engagementPermissions(project);
  const allowed = MANAGER_PHASES.includes(phase) ? permissions.manage : permissions.edit;
  return !!allowed && (phase === 'acceptance' || !!permissions.unlocked) && project.status !== 'closed' && project.status !== 'completed';
}

export function formatHours(hours) {
  const value = Math.round((Number(hours) || 0) * 100) / 100;
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2).replace(/0$/, '');
}

// Engagements can switch off the budget and the Observations on main tasks (both on by default)
export function budgetEnabled(project) {
  return !!project && project.include_budget !== false && project.include_budget !== 0;
}

export function observationsEnabled(project) {
  return !!project && project.include_observations !== false && project.include_observations !== 0;
}

// Phase progress, shared by the project hero and the Overview
export function phaseSteps(engagement, phase) {
  return ((engagement && engagement.steps) || []).filter(step => step.phase === phase);
}

export function phaseIncluded(engagement, phase) {
  return phaseSteps(engagement, phase).filter(step => step.included).length;
}

export function phaseCompleted(engagement, phase) {
  return phaseSteps(engagement, phase).filter(step => step.included && step.status === 'completed').length;
}

export function currentPhase(engagement) {
  if (engagement.requires_acceptance && !engagement.accepted_at) {
    return 'acceptance';
  }
  const open = PHASE_KEYS.find(phase => phaseIncluded(engagement, phase) > phaseCompleted(engagement, phase));
  return open || PHASE_KEYS[PHASE_KEYS.length - 1];
}

// completed, current, open, locked or skipped
export function phaseState(engagement, phase) {
  if (!phaseIncluded(engagement, phase)) {
    return 'skipped';
  }
  if (phase !== 'acceptance' && !engagement.unlocked) {
    return 'locked';
  }
  if (phase === 'acceptance' && engagement.requires_acceptance) {
    return engagement.accepted_at ? 'completed' : 'current';
  }
  if (phaseCompleted(engagement, phase) === phaseIncluded(engagement, phase)) {
    return 'completed';
  }
  return phase === currentPhase(engagement) ? 'current' : 'open';
}

// The tab where a phase is worked on
export const PHASE_TABS = { acceptance: 'acceptance', planning: 'planning', execution: 'tasks', reporting: 'reporting' };
